import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../../config/api';
import {
  AUTH_EXPIRED_EVENT,
  clearAccessToken,
  getStoredAccessToken,
  getStoredAccessTokenUser,
  getStoredAccessTokenSource,
  installAuthInterceptors,
  isDefinitiveSessionFailure,
  logoutServerSession,
  refreshAccessToken,
  storeAccessToken,
} from './authSession';

const emptyUserDetails = { firstName: '', lastName: '', email: '', phone: '', role: '' };
const impersonationReturnTokenKey = 'quizify.impersonation.returnToken';
const impersonationReturnStorageKey = 'quizify.impersonation.returnStorage';
const impersonationTargetKey = 'quizify.impersonation.target';
const sessionRestoreRetryDelay = 5000;

const clearImpersonationSession = () => {
  sessionStorage.removeItem(impersonationReturnTokenKey);
  sessionStorage.removeItem(impersonationReturnStorageKey);
  sessionStorage.removeItem(impersonationTargetKey);
};

const normalizeUser = (user = {}) => ({
  id: user._id || user.id,
  firstName: user.firstName || '',
  lastName: user.lastName || '',
  email: user.email || '',
  phone: user.phone || '',
  role: user.role || '',
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(emptyUserDetails);
  const [isImpersonating, setIsImpersonating] = useState(Boolean(sessionStorage.getItem(impersonationReturnTokenKey)));
  const navigate = useNavigate();

  const clearSession = useCallback((redirectPath = null) => {
    setIsAuthenticated(false);
    setUserDetails(emptyUserDetails);
    clearAccessToken();
    clearImpersonationSession();
    setIsImpersonating(false);
    setIsAuthLoading(false);

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);

  useEffect(() => installAuthInterceptors(), []);

  useEffect(() => {
    const handleAuthExpired = () => clearSession('/sign-in');
    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);

    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
  }, [clearSession]);

  useEffect(() => {
    let isMounted = true;
    let retryTimer = null;

    const scheduleRestoreRetry = (restoreSession) => {
      if (!isMounted || retryTimer) {
        return;
      }

      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        restoreSession();
      }, sessionRestoreRetryDelay);
    };

    const restoreSession = async () => {
      if (!isMounted) {
        return;
      }

      setIsAuthLoading(true);

      try {
        if (!getStoredAccessToken()) {
          await refreshAccessToken();
        }

        const response = await axios.get(`${apiUrl}/me`);

        if (!isMounted) {
          return;
        }

        setIsAuthenticated(true);
        setUserDetails(normalizeUser(response.data.user));
        setIsImpersonating(Boolean(sessionStorage.getItem(impersonationReturnTokenKey)));
        setIsAuthLoading(false);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (isDefinitiveSessionFailure(error)) {
          clearSession();
          return;
        }

        const accessTokenUser = getStoredAccessTokenUser();

        if (accessTokenUser) {
          setIsAuthenticated(true);
          setUserDetails(normalizeUser(accessTokenUser));
          setIsAuthLoading(false);
          return;
        }

        scheduleRestoreRetry(restoreSession);
      }
    };

    const handleOnline = () => {
      if (retryTimer) {
        window.clearTimeout(retryTimer);
        retryTimer = null;
      }

      restoreSession();
    };

    restoreSession();
    window.addEventListener('online', handleOnline);

    return () => {
      isMounted = false;
      window.removeEventListener('online', handleOnline);

      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [clearSession]);

  const loginMe = useCallback((userData, accessToken) => {
    if (accessToken) {
      storeAccessToken(accessToken);
    }

    clearImpersonationSession();
    setIsImpersonating(false);
    setIsAuthenticated(true);
    setIsAuthLoading(false);
    setUserDetails(normalizeUser(userData));
    navigate('/dashboard');
  }, [navigate]);

  const updateUserDetails = useCallback((userData) => {
    setUserDetails(normalizeUser(userData));
  }, []);

  const logoutMe = useCallback(async () => {
    try {
      await logoutServerSession();
    } catch (error) {
      console.error('Errore durante il logout:', error);
    } finally {
      clearSession('/');
    }
  }, [clearSession]);

  const stopImpersonating = useCallback(async () => {
    const returnToken = sessionStorage.getItem(impersonationReturnTokenKey);

    clearImpersonationSession();
    setIsImpersonating(false);

    if (!returnToken) {
      setIsAuthLoading(true);

      try {
        const refreshedSession = await refreshAccessToken();
        const response = await axios.get(`${apiUrl}/me`, {
          headers: { Authorization: `Bearer ${refreshedSession.accessToken}` },
        });
        setIsAuthenticated(true);
        setUserDetails(normalizeUser(response.data.user));
        navigate('/user-list', { replace: true });
      } catch (error) {
        if (isDefinitiveSessionFailure(error)) {
          clearSession('/sign-in');
        }
      } finally {
        setIsAuthLoading(false);
      }
      return;
    }

    storeAccessToken(returnToken);
    setIsAuthLoading(true);

    try {
      const response = await axios.get(`${apiUrl}/me`);
      setIsAuthenticated(true);
      setUserDetails(normalizeUser(response.data.user));
      navigate('/user-list', { replace: true });
    } catch (error) {
      if (isDefinitiveSessionFailure(error)) {
        clearSession('/sign-in');
      } else {
        const accessTokenUser = getStoredAccessTokenUser();

        if (accessTokenUser) {
          setIsAuthenticated(true);
          setUserDetails(normalizeUser(accessTokenUser));
        }
      }
    } finally {
      setIsAuthLoading(false);
    }
  }, [clearSession, navigate]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isAuthLoading,
      userDetails,
      isImpersonating,
      loginMe,
      logoutMe,
      updateUserDetails,
      stopImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const startImpersonationSession = ({ token, returnToken, returnStorage, targetUser }) => {
  sessionStorage.setItem(impersonationReturnTokenKey, returnToken);
  sessionStorage.setItem(impersonationReturnStorageKey, returnStorage || 'session');
  sessionStorage.setItem(impersonationTargetKey, JSON.stringify(targetUser || null));
  storeAccessToken(token);
};

export const getCurrentTokenSource = getStoredAccessTokenSource;

export const useAuth = () => useContext(AuthContext);
